import { userService } from "@/apis/user.service";

const createEmptyMeasurement = () => ({
  measures: null,
  brand: null,
  model: null,
});

const initialQuotationForm = () => ({
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  postcode: "",
  houseNumber: "",
  houseLetter: "",
  remarks: "",
  measurements: [createEmptyMeasurement()],
});

const initialContactForm = () => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  contactMethod: "",
  preferredDate: "",
  preferredTime: "",
  postcode: "",
  houseNumber: "",
  houseLetter: "",
  remarks: "",
});

const initialState = () => ({
  currentScreen: "idle",
  selectedCategory: null,        // single category ID selected on the category screen
  kioskCategories: [],           // [{ id, name, icon }] fetched from /api/kiosk-categories/
  kioskCategoriesLoading: false,
  categoryMeasures: [],          // [{ id, name, item_id }] fetched from /api/kiosk-measures/<category_id>/
  categoryMeasuresLoading: false,
  lastActivity: null,
  kioskVariant: "general",       // "general" | "category"
  preselectedCategory: null,
  wordpressEmbedUrl: null,       // URL loaded into KioskWordpressEmbed iframe
  wordpressEmbedTitle: null,     // Accessible title for the iframe
  quotationPrefill: null,        // { measureId, brand, model } set by WordPress embed before navigating
  quotationForm: initialQuotationForm(),
  contactForm: initialContactForm(),
});

const mutations = {
  SET_SCREEN(state, screenId) {
    state.currentScreen = screenId;
  },
  SET_SELECTED_CATEGORY(state, categoryId) {
    state.selectedCategory = categoryId;
  },
  SET_KIOSK_CATEGORIES(state, categories) {
    state.kioskCategories = categories;
  },
  SET_KIOSK_CATEGORIES_LOADING(state, loading) {
    state.kioskCategoriesLoading = loading;
  },
  SET_CATEGORY_MEASURES(state, measures) {
    state.categoryMeasures = measures;
  },
  SET_CATEGORY_MEASURES_LOADING(state, loading) {
    state.categoryMeasuresLoading = loading;
  },
  UPDATE_ACTIVITY(state) {
    state.lastActivity = Date.now();
  },
  SET_KIOSK_VARIANT(state, variant) {
    state.kioskVariant = variant;
  },
  SET_PRESELECTED_CATEGORY(state, categoryId) {
    state.preselectedCategory = categoryId;
  },
  SET_WORDPRESS_EMBED(state, { url, title }) {
    state.wordpressEmbedUrl = url;
    state.wordpressEmbedTitle = title;
  },
  SET_QUOTATION_PREFILL(state, prefill) {
    state.quotationPrefill = prefill;
  },
  SET_QUOTATION_FORM(state, data) {
    state.quotationForm = { ...state.quotationForm, ...data };
  },
  SET_CONTACT_FORM(state, data) {
    state.contactForm = { ...state.contactForm, ...data };
  },
  RESET_SESSION(state) {
    const fresh = initialState();
    // Preserve variant config and fetched categories across session resets
    const kioskVariant = state.kioskVariant;
    const preselectedCategory = state.preselectedCategory;
    const kioskCategories = state.kioskCategories;
    Object.keys(fresh).forEach((key) => {
      state[key] = fresh[key];
    });
    state.kioskVariant = kioskVariant;
    state.preselectedCategory = preselectedCategory;
    state.kioskCategories = kioskCategories;
    // For category-specific kiosks, restore the pre-selected category so the
    // feature hub and quotation form continue to work after a session reset.
    if (kioskVariant === "category" && preselectedCategory) {
      state.selectedCategory = preselectedCategory;
    }
  },
};

const actions = {
  navigateTo({ commit }, screenId) {
    commit("SET_SCREEN", screenId);
  },
  setSelectedCategoryAction({ commit }, categoryId) {
    commit("SET_SELECTED_CATEGORY", categoryId);
  },
  updateActivityAction({ commit }) {
    commit("UPDATE_ACTIVITY");
  },
  resetSessionAction({ commit }) {
    commit("RESET_SESSION");
  },
  setKioskVariantAction({ commit }, variant) {
    commit("SET_KIOSK_VARIANT", variant);
  },
  setPreselectedCategoryAction({ commit }, categoryId) {
    commit("SET_PRESELECTED_CATEGORY", categoryId);
  },
  navigateToWordpressEmbedAction({ commit }, { url, title, screenId }) {
    commit("SET_WORDPRESS_EMBED", { url, title });
    commit("SET_SCREEN", screenId);
  },
  setQuotationPrefillAction({ commit }, prefill) {
    commit("SET_QUOTATION_PREFILL", prefill);
  },

  async fetchKioskCategoriesAction({ commit, state }) {
    // Skip if already loaded
    if (state.kioskCategories.length > 0) return;
    commit("SET_KIOSK_CATEGORIES_LOADING", true);
    try {
      const response = await userService.getKioskCategories();
      if (response.isSuccess) {
        const categories = response.res.data || [];
        // Convert AWS image URLs to base64 data URLs so they don't expire
        const categoriesWithCachedImages = await Promise.all(
          categories.map(async (cat) => {
            if (!cat.image) return cat;
            try {
              const imgResponse = await fetch(cat.image);
              const blob = await imgResponse.blob();
              const dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              return { ...cat, image: dataUrl };
            } catch {
              return cat;
            }
          })
        );
        commit("SET_KIOSK_CATEGORIES", categoriesWithCachedImages);
      }
    } finally {
      commit("SET_KIOSK_CATEGORIES_LOADING", false);
    }
  },

  async fetchCategoryMeasuresAction({ commit }, categoryId) {
    if (!categoryId) return;
    commit("SET_CATEGORY_MEASURES_LOADING", true);
    try {
      const response = await userService.getKioskMeasuresByCategory(categoryId);
      if (response.isSuccess) {
        commit("SET_CATEGORY_MEASURES", response.res.data);
      }
    } finally {
      commit("SET_CATEGORY_MEASURES_LOADING", false);
    }
  },

  async submitQuotationAction({ commit, state }, formData) {
    // Preserve form data for the success screen before the API call
    commit("SET_QUOTATION_FORM", formData);

    const measurements = (formData.measurements || []).map((m) => ({
      measure_id: m.measures,
      brand_id:   m.brand || null,
      model_id:   m.model || null,
    }));

    const payload = {
      first_name:   formData.firstName,
      last_name:    formData.lastName || "",
      email:        formData.email || "",
      phone_number: formData.mobile || "",
      zipcode:      formData.postcode,
      house_number: formData.houseNumber,
      house_letter: formData.houseLetter || "",
      measurements,
    };

    if (formData.remarks) payload.remarks = formData.remarks;

    const queryParams = process.env.VUE_APP_KIOSK_DOMAIN
      ? { domain: process.env.VUE_APP_KIOSK_DOMAIN }
      : {};

    await userService.submitKioskQuotation(payload, queryParams);

    commit("SET_SCREEN", "quotation-success");
  },

  async submitContactAction({ commit }, formData) {
    // Preserve form data for the success screen before the API call
    commit("SET_CONTACT_FORM", formData);

    const payload = {
      first_name:               formData.firstName,
      last_name:                formData.lastName,
      email:                    formData.email || "",
      phone_number:             formData.phone || "",
      preferred_contact_method: formData.contactMethod,
    };

    if (formData.preferredDate) payload.preferred_date = formData.preferredDate;
    if (formData.preferredTime) payload.preferred_time = formData.preferredTime;
    if (formData.postcode)      payload.zipcode = formData.postcode;
    if (formData.houseNumber)   payload.house_number = formData.houseNumber;
    if (formData.houseLetter)   payload.house_letter = formData.houseLetter;
    if (formData.remarks)       payload.remarks = formData.remarks;

    const queryParams = process.env.VUE_APP_KIOSK_DOMAIN
      ? { domain: process.env.VUE_APP_KIOSK_DOMAIN }
      : {};

    await userService.submitKioskContactRequest(payload, queryParams);

    commit("SET_SCREEN", "contact-success");
  },
};

// eslint-disable-next-line import/prefer-default-export
export const kiosk = {
  namespaced: true,
  state: initialState(),
  mutations,
  actions,
};
